import React, { useEffect, useMemo, useState } from "react";
import { FRAGMENT_MIN_HEIGHT } from "components/utils";
import { ContentEditable } from "components/elements/ContentEditable";
import { Ico } from "components/Uikit";
import {
    ActionButtonSC,
    ActionsColumnSC,
    BrokenTimeSC,
    IconWrapperSC,
    PhraseContinuesSC,
    PhrasePieceBadgeSC,
    SkippedSC,
    SpeakerBadgeSC,
    SubtitleContainerSC,
} from "./styled";
import { useAudioStateContext } from "hooks/useAudioStateContext";
import { useAudioHandlesContext } from "hooks/useAudioHandlesContext";
import { SpellCheckMIcon } from "components/elements/icons/SpellCheckMIcon";
import { ForwardMIcon } from "components/elements/icons/ForwardMIcon";
import { AudioTrack, MetadataToSubtitlesLanguages } from "providers/AudioProvider/types";
import { AudioManage } from "./components/AudioManage/AudioManage";
import { CreateIntervalSubtitle } from "./components/CreateIntervalSubtitle/CreateIntervalSubtitle";
import { TranslatedSplitModal } from "../TranslatedSplitModal";
import { useUiAudioStateContext } from "../../../../../../../hooks/useUiAudioStateContext";
import { CheckNeedMIcon } from "../../../../../../elements/icons/CheckNeedMIcon";
import { useCorrection } from "../../../../../../../providers/CorrectionProvider";
import { Correction } from "../../../../../../../api/fastApi/commentsTypes";
import { createSkipSubtitleApi, unskipSubtitleApi } from "api/fastApi/translateApi";
import { formatCueTime } from "providers/AudioProvider/editorErrors";
import { getTextDirection } from "utils/textDirection";
import { useUiLang } from "utils/uiLanguage";
import { TranslateSubtitleRequest } from "providers/AudioProvider/handleTypes";
import { cueTimingBroken, formatWindowTime, rowVoicingBlockReason, rowVoicingCheckWarning } from "./cueActions";
import { phrasePieceRepeatRelation, phrasePieceTranslateWarning } from "providers/AudioProvider/helpers";
import { cueSpeakerLabel, cueSpeakerTitle } from "providers/AudioProvider/cueSpeaker";

interface ISubtitleBoxProps {
    oneTrack: AudioTrack;
    zoomLevel: number;
    openEditor: (track: AudioTrack) => void;
    onTranslate?: (arg: TranslateSubtitleRequest) => Promise<unknown>;
    isRightSide?: boolean;
    isFullWidth?: boolean;
    /** Normal-flow editor in the collision review, independent of cue duration. */
    inline?: boolean;
}

export const SubtitleBox = ({
    oneTrack,
    zoomLevel,
    openEditor,
    isRightSide,
    onTranslate,
    isFullWidth,
    inline,
}: ISubtitleBoxProps) => {
    const handles = useAudioHandlesContext();
    const state = useAudioStateContext();
    const uiState = useUiAudioStateContext();
    const { addCorrection, correctionsForMedia, updateCorrectionStatus } = useCorrection();
    const [editing, setEditing] = useState("");
    const [editedText, setEditedText] = useState("");
    const [translatedSplitOpen, setTranslatedSplitOpen] = useState(false);
    const [unskipping, setUnskipping] = useState(false);
    const hideEditors = false;
    const en = useUiLang() === "en";

    const isSearchMatch = useMemo(() => {
        if (!uiState.searchQuery) return false;
        const q = uiState.searchQuery.toLowerCase();
        const text = isRightSide ? oneTrack.translation : oneTrack.captions;
        return text ? text.toLowerCase().includes(q) : false;
    }, [uiState.searchQuery, isRightSide, oneTrack.translation, oneTrack.captions]);

    const correction = useMemo(() => {
        return correctionsForMedia.find((item) => {
            if (isRightSide) {
                return checkCorrectionTime({ item, oneTrack, isRightSide, lang: state.lang });
            } else {
                return checkCorrectionTime({
                    item,
                    oneTrack,
                    isRightSide: false,
                    lang: state.originalLang,
                });
            }
        });
    }, [
        correctionsForMedia,
        isRightSide,
        oneTrack.start,
        oneTrack.translationStart,
        state.lang,
        state.originalLang,
    ]);

    // hanlde input only when the element is not focused.
    // It happens when the value is changed from find and replace browser addon
    const handleInput = (text: string, focused: boolean, oneTrack: AudioTrack) => {
        setEditing(oneTrack.original_id);
        if (state.savingStatus || hideEditors) {
            return;
        }
        if (text === oneTrack.captions) {
            setEditedText(text);
        }
    };
    // «→» — машинный перевод реплики. Уже написанный текст заменяется только после вопроса (и
    // тогда сервер получает overwrite), в режиме «Без автоправок» вопрос задаётся всегда (и
    // сервер получает явную просьбу). 30.09 на fdcde2a1 два нажатия без подписи и без вопроса
    // заменили текст переводчика машинным.
    const handleCreateTranslate = async () => {
        if (state.translatedSubtitleId === oneTrack.original_id || !!editing) {
            return;
        }
        // Текст, который заменит перевод: сохранённый — или набранный и ещё не сохранённый (у
        // реплики без пары старый сервер его не принял, и в строке его нет).
        const existing =
            [handles.unsavedTranslation?.(oneTrack.original_id), oneTrack.translation]
                .map((text) => (text || "").trim())
                .find(Boolean) || "";
        const manual = Boolean(state.manualOnly);
        // Кусок с общей русской фразой: «→» переведёт ВСЮ фразу в этот кусок, а у других кусков
        // свои части — фраза прозвучит дважды (так 01.10 перезаписан «Pienso:»). Спрашиваем всегда.
        const pieceWarning = phrasePieceTranslateWarning(oneTrack, en);
        if (existing || manual || pieceWarning) {
            const parts: string[] = [];
            if (pieceWarning) {
                parts.push(pieceWarning);
            }
            if (existing) {
                const shown = existing.length > 300 ? `${existing.slice(0, 300)}…` : existing;
                parts.push(
                    en
                        ? `Replace the translation with a machine translation?\n\n«${shown}»\n\nThis text will be overwritten.`
                        : `Заменить текст перевода машинным переводом?\n\n«${shown}»\n\nЭтот текст будет перезаписан.`,
                );
            } else {
                parts.push(en ? "Machine-translate this cue?" : "Сделать машинный перевод этой реплики?");
            }
            if (manual) {
                parts.push(
                    en
                        ? "The pair is in «No automatic edits — manual work only»: the machine translation runs only because you ask for it."
                        : "Пара в режиме «Без автоправок — только ручная работа»: машинный перевод будет сделан только по вашей просьбе.",
                );
            }
            if (!window.confirm(parts.join("\n\n"))) {
                return;
            }
        }
        await onTranslate?.({
            toLang: state.lang,
            fromLang: state.originalLang,
            trackId: oneTrack.original_id,
            overwrite: Boolean(existing),
            overrideManualOnly: manual,
        });
    };

    // ⊘ — снять реплику с озвучки: её перевод и озвучка не попадут в видео. 01.10.2026 (fdcde2a1,
    // ru_en, папка 3, 2:10) кнопка рядом с «→» сняла реплику без единого вопроса и без единого
    // слова на экране: перевод краснел, ♪ падал, редактор играл старый дубль с другим текстом.
    // Теперь реплику с переводом или озвучкой ⊘ снимает только после вопроса, а снятая говорит
    // это словами (метка «снята с озвучки» в колонке перевода) и возвращается ♪ или «вернуть».
    const skipped = oneTrack.translation_skip === true;

    const handleCreateSkip = async () => {
        if (state.translatedSubtitleId === oneTrack.original_id || !!editing || skipped) {
            return;
        }
        // Что пропадёт из видео: сохранённый перевод или набранный и ещё не сохранённый, и дубль.
        const existing =
            [handles.unsavedTranslation?.(oneTrack.original_id), oneTrack.translation]
                .map((text) => (text || "").trim())
                .find(Boolean) || "";
        const voiced = oneTrack.translation_voiced === true;
        if (existing || voiced) {
            const shown = existing.length > 300 ? `${existing.slice(0, 300)}…` : existing;
            const what =
                existing && voiced
                    ? en
                        ? `The translation «${shown}» and its voicing will not be in the video.`
                        : `Перевод «${shown}» и её озвучка не попадут в видео.`
                    : existing
                      ? en
                          ? `The translation «${shown}» will not be in the video.`
                          : `Перевод «${shown}» не попадёт в видео.`
                      : en
                        ? "Its voicing will not be in the video."
                        : "Её озвучка не попадёт в видео.";
            const at = formatCueTime(oneTrack.start);
            // Без текста ♪ снятую реплику не вернёт: ♪ заперт пустым текстом, сервер отказал бы
            // skipped_without_text. Тогда вопрос называет только «вернуть».
            const back = existing
                ? en
                    ? "You can bring it back with ♪ or «restore»."
                    : "Вернуть можно кнопкой ♪ или «вернуть»."
                : en
                  ? "You can bring it back with «restore»."
                  : "Вернуть можно кнопкой «вернуть».";
            const question = en
                ? `Remove the cue at ${at} from voicing? ${what} ${back}`
                : `Снять реплику ${at} с озвучки? ${what} ${back}`;
            if (!window.confirm(question)) {
                return;
            }
        }
        // Несохранённая правка сначала ложится на диск: сервер откладывает при снятии ровно
        // последний текст, а поздний автосейв не впишет текст в уже снятую реплику. Не легла —
        // причину уже назвало само сохранение, и реплика не снимается.
        try {
            await handles.flushGate?.();
        } catch {
            return;
        }
        try {
            await createSkipSubtitleApi({
                mediaId: state.mediaId,
                id: oneTrack.original_id,
                folder_num: state.folder,
                lang: state.lang,
                start: oneTrack.start,
                end: oneTrack.start + oneTrack.length,
                language_from: MetadataToSubtitlesLanguages[state.originalLang],
            });
        } catch (e) {
            handles.showEditorError(
                `${en ? "The cue is not removed from voicing" : "Реплика не снята с озвучки"}: ${(e as Error).message}`,
            );
            return;
        }
        handles.refreshAllSubtitles(state.lang);
    };

    // Метка снятой реплики называет и текст, который не попадёт в видео: новый сервер прячет его в
    // `skipped_captions`, и поле перевода у снятой реплики пустое.
    const skippedText = (oneTrack.translation || "").trim() || (oneTrack.translation_skipped_text || "").trim();
    const skippedWhat = skippedText
        ? en
            ? `its translation «${skippedText}» and its voicing`
            : `её перевод «${skippedText}» и озвучка`
        : en
          ? "its translation and voicing"
          : "её перевод и озвучка";
    // ♪ возвращает снятую, только если ему есть что озвучить; без текста — только «вернуть».
    const skippedTitle = en
        ? `This cue is removed from voicing: ${skippedWhat} will not be in the video. «restore» takes the mark off` +
          (skippedText ? "; ♪ voices it again and brings it back too." : ".")
        : `Реплика снята с озвучки: ${skippedWhat} не попадут в видео. «вернуть» снимет пометку` +
          (skippedText ? "; ♪ озвучит её заново и тоже вернёт." : ".");

    // «вернуть» — снять метку пропуска (новая ручка сервера). Старый сервер ручки не знает: его
    // ответ показывается в красной полосе как есть, папка не перечитывается.
    const handleUnskip = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (unskipping) {
            return;
        }
        setUnskipping(true);
        try {
            await unskipSubtitleApi({
                mediaId: state.mediaId,
                id: oneTrack.original_id,
                folder_num: state.folder,
                lang: state.lang,
                language_from: MetadataToSubtitlesLanguages[state.originalLang],
            });
        } catch (err) {
            handles.showEditorError(
                `${en ? "The cue is not returned to voicing" : "Реплика не возвращена в озвучку"}: ${(err as Error).message}`,
            );
            return;
        } finally {
            setUnskipping(false);
        }
        handles.refreshAllSubtitles(state.lang);
    };

    const onBlur = (text: string) => {
        uiState.setActiveCaptionTrack("");
        if (hideEditors) {
            return;
        }
        setEditing("");

        if (isRightSide && text === oneTrack.translation) {
            return;
        }
        // Пустое поле перевода у реплики без текста — не правка. Новая реплика «+» сама получает
        // фокус в пустом поле; уход из него слал captions "" на реплику без пары, старый сервер
        // отказывал, и отказ запирал все операции редактора.
        if (isRightSide && !(text || "").trim() && !(oneTrack.translation || "").trim()) {
            return;
        }
        if (!isRightSide && text === oneTrack.captions) {
            return;
        }
        if (!isRightSide) {
            void handles.editRusSubtitle({
                captions: text,
                track: oneTrack,
                lang: MetadataToSubtitlesLanguages[state.originalLang],
            });
        }
        if (isRightSide) {
            handles.editTranslatedSubtitle({
                captions: text,
                id: oneTrack.original_id,
                lang: MetadataToSubtitlesLanguages[state.lang],
                update_recommendation: false,
            });
        }
    };

    const handleClick = () => {
        uiState.setActiveCaptionTrack(oneTrack.id);
        handles.setLastActiveSubtitle({
            playOriginalStart: isRightSide ? oneTrack.original_start : oneTrack.start,
            playbackTime: oneTrack.start,
        });
        if (isRightSide) {
            handles.setLastActiveVoiceover({ trackId: oneTrack.id, playOriginalStart: oneTrack.original_start, playbackTime: oneTrack.start });
        } else {
            // Also set voiceover so Right shortcut plays from the same row
            const translatedTracks = state.translationTrackAll[state.lang];
            const corresponding = translatedTracks?.find((t: AudioTrack) => t.original_id === oneTrack.original_id);
            if (corresponding?.file) {
                handles.setLastActiveVoiceover({ trackId: corresponding.id, playOriginalStart: corresponding.original_start, playbackTime: corresponding.start });
            }
        }
    };

    useEffect(() => {
        if (!state.subtitleEditing) {
            setEditing("");
        }
    }, [state.subtitleEditing]);

    // Испорченное время реплики (своё или у перевода) видно на ней самой: 30.09 такая реплика
    // выглядела обычной полоской в 25 px. Что именно испорчено — словами сервера. «Вернуть окно»
    // предлагается только там, где сломано окно (время реплики оригинала или отрицательные место
    // и слот перевода); дубль с длиной ≤ 0 при целом окне чинит переозвучка, а не окно — оно
    // стёрло бы законную раскладку.
    const sourceTimeBroken = !isRightSide && cueTimingBroken(oneTrack);
    const translationTimeBroken = !isRightSide && oneTrack.translation_timing_invalid === true;
    const translationWindowBroken = translationTimeBroken && oneTrack.translation_window_broken === true;
    const canRestoreWindow = sourceTimeBroken || translationWindowBroken;
    const sourceProblem = oneTrack.timing_problem ? ` (${oneTrack.timing_problem})` : "";
    const pairProblem = oneTrack.translation_timing_problem || (en ? "length ≤ 0" : "длина ≤ 0");
    const brokenTimeTitle = sourceTimeBroken
        ? en
            ? `This cue's time is broken${sourceProblem}: start ${formatWindowTime(oneTrack.start)}, length ${Math.round(oneTrack.length)} ms. The player skips it and voicing is refused. «Restore window» puts it between its neighbours.`
            : `Время реплики испорчено${sourceProblem}: начало ${formatWindowTime(oneTrack.start)}, длина ${Math.round(oneTrack.length)} мс. Плеер её не играет, озвучка откажет. «Вернуть окно» поставит её между соседними репликами.`
        : translationWindowBroken
          ? en
              ? `The translation's time is broken (${pairProblem}). The player skips it and voicing is refused. «Restore window» gives the translation this cue's window; a voicing place that is not before the folder's start stays.`
              : `Время перевода испорчено (${pairProblem}). Плеер его не играет, озвучка откажет. «Вернуть окно» вернёт переводу окно этой реплики; место озвучки, если оно не раньше начала папки, останется.`
          : en
            ? `The voicing's length is broken (${pairProblem}): the player skips it. Voice the cue again (♪) — the synthesis recomputes the length; the window is fine.`
            : `Длина озвучки перевода испорчена (${pairProblem}): плеер её пропускает. Переозвучьте реплику (♪) — синтез пересчитает длину; окно у реплики целое.`;

    // Кусок фразы (раскладка дубляжа по фразам): русский текст у кусков общий — вся фраза, а
    // перевод и озвучка у каждого свои. Колонка оригинала показывала фразу 2–5 раз подряд, и
    // 01.10.2026 переводчица удалила 10 кусков как дубли (4119e839, ru_es, папка 21). Теперь
    // кусок, повторяющий русский текст более раннего куска, — одна строка «продолжение фразы i/n»
    // (фраза в подсказке), а кусок со своим русским текстом — редактор и метка «фраза i/n».
    // Колонка перевода не меняется: там у каждого куска своя часть.
    // Кусок, чей перевод повторяет перевод соседнего (043f05ea, ru_en, папка 24: весь перевод
    // фразы в каждом из четырёх кусков), — настоящий повтор в дубляже: строка и метка говорят
    // «⚠ повтор», а не «продолжение» и не «не дубль».
    const pieceIndex = oneTrack.piece_index ?? 0;
    const pieceCount = oneTrack.piece_count ?? 0;
    const isPhrasePiece = !isRightSide && pieceCount > 1 && pieceIndex > 0;
    const continuesPhrase = isPhrasePiece && oneTrack.piece_repeats_text === true;
    const dubRepeats = isPhrasePiece ? oneTrack.piece_dub_repeats ?? [] : [];
    const isDubRepeat = dubRepeats.length > 0;
    const dubRepeatWhole = isDubRepeat && oneTrack.piece_dub_repeats_whole === true;
    const dubRepeatRelation = isDubRepeat
        ? phrasePieceRepeatRelation(dubRepeats, dubRepeatWhole, pieceCount, en)
        : "";
    const phrasePieceTitle = isDubRepeat
        ? en
            ? `Piece ${pieceIndex} of ${pieceCount} of a phrase, but its translation ${dubRepeatRelation} — a real repeat in the dub. Delete the extra piece.`
            : `Кусок ${pieceIndex} из ${pieceCount} фразы, но его перевод ${dubRepeatRelation} — это настоящий повтор в дубляже. Удалите лишний кусок.`
        : en
          ? `Piece ${pieceIndex} of ${pieceCount} of one phrase — not a duplicate: the pieces share the Russian text, but each has its own part of the translation and its own voicing.`
          : `Кусок ${pieceIndex} из ${pieceCount} одной фразы — не дубль: русский текст у кусков общий, а перевод и озвучка у каждого свои.`;
    const dubRepeatWhat = dubRepeatWhole
        ? en
            ? "the whole phrase's translation"
            : "в куске весь перевод фразы"
        : en
          ? `same translation as piece ${dubRepeats[0]}`
          : `перевод как у куска ${dubRepeats[0]}`;
    const phraseLineText = isDubRepeat
        ? en
            ? `⚠ phrase repeat ${pieceIndex}/${pieceCount} — ${dubRepeatWhat}`
            : `⚠ повтор фразы ${pieceIndex}/${pieceCount} — ${dubRepeatWhat}`
        : en
          ? `↳ phrase continues ${pieceIndex}/${pieceCount}`
          : `↳ продолжение фразы ${pieceIndex}/${pieceCount}`;
    const phraseBadgeText = en
        ? `phrase ${pieceIndex}/${pieceCount}${isDubRepeat ? " · ⚠ repeat" : ""}`
        : `фраза ${pieceIndex}/${pieceCount}${isDubRepeat ? " · ⚠ повтор" : ""}`;

    const handleRestoreWindow = (e: React.MouseEvent) => {
        e.stopPropagation();
        handles.restoreCueWindow(oneTrack.original_id);
    };

    const handleClickRecommendation = () => {
        handles.setEditedRecommendationSubtitle(oneTrack.original_id);
    };

    const handleEditSubtitle = () => {
        handles.setSubtitleMode(true);
        openEditor(oneTrack);
    };

    // Base direction of the text in THIS box: the right column carries the translation,
    // the left the source. See getTextDirection for what an unset base direction costs.
    const captionsDir = getTextDirection(isRightSide ? state.lang : state.originalLang);

    const handleDeleteTranslatedSubtitle = () => {
        handles.deleteTranslatedSubtitle({ id: oneTrack.original_id, selectLang: state.lang });
    };

    // Translated-led split/merge (right column): same busy guards the neighbours use.
    const translatedBusy =
        state.savingStatus ||
        state.translatedSubtitleId === oneTrack.original_id ||
        editing === oneTrack.original_id;

    const handleOpenTranslatedSplit = () => {
        if (translatedBusy) {
            return;
        }
        setTranslatedSplitOpen(true);
    };

    const handleMergeTranslated = () => {
        if (translatedBusy) {
            return;
        }
        void handles.mergeTranslatedCaptions(oneTrack);
    };

    const handleOpusSplit = () => {
        if (translatedBusy) {
            return;
        }
        const isConfirmed = window.confirm(
            "Разбить эту реплику по паузам лектора? Решение примет Opus, все части будут переозвучены.",
        );
        if (!isConfirmed) {
            return;
        }
        void handles.opusSplitTranslatedSubtitle({
            id: oneTrack.original_id,
            folder_num: parseInt(oneTrack.folder_num),
        });
    };

    const handleCheckTranslatedSubtitle = () => {
        if (correction) {
            updateCorrectionStatus({
                mediaId: state.mediaId,
                status: "done",
                correctionId: correction.uuid,
            });
        } else {
            addCorrection(state.mediaId, {
                folder_num: state.folder.toString(),
                subtitle_start_time: oneTrack.translationStart || 0,
                language: state.lang,
                comment: "",
                subtitle_text: isRightSide ? oneTrack.translation : oneTrack.captions,
            });
        }
    };

    return (
        <SubtitleContainerSC
            data-cue-id={oneTrack.id}
            data-caption-side={isRightSide ? "translation" : "source"}
            // Строка текста (и перевода) стоит в колонке оригинала — там, где это сказал лектор: её высота — слот
            // РУССКОЙ реплики, а не дубль (длину дубля показывает дорожка озвучки, `data-voicing-cue`/`data-length-ms`).
            data-slot-ms={Math.round(oneTrack.length)}
            dir={captionsDir}
            style={{
                height: `${Math.max(oneTrack.length / zoomLevel, FRAGMENT_MIN_HEIGHT)}px`,
                ...(inline ? { position: "relative", left: 0, right: "auto", minWidth: 0, width: "100%", height: 160, opacity: 1 } as React.CSSProperties : {}),
                // Under dir="rtl" the start edge IS the right edge, so this keeps the
                // right-hand alignment Arabic already had while the base direction now
                // does the bidi work the old `text-align: end` could never do.
                textAlign: captionsDir === "rtl" ? "start" : "left",
                paddingRight: captionsDir === "rtl" ? "10px" : "0",
            }}
            className={`${
                (state.trackSelected === oneTrack.original_id ||
                    (isRightSide && state.translatedTrackSelected === oneTrack.original_id)) &&
                "selected"
            }`}
            right={isRightSide?.toString()}
            full={isFullWidth?.toString()}
            isactive={
                uiState.activeCaptionTrack
                    ? (uiState.activeCaptionTrack === oneTrack.id).toString()
                    : "none"
            }
            red={correction ? "red" : "none"}
            searchhighlight={isSearchMatch ? "true" : "false"}
        >
            {continuesPhrase ? (
                <PhraseContinuesSC
                    data-phrase-continues="true"
                    data-phrase-repeat={isDubRepeat ? "true" : undefined}
                    $repeat={isDubRepeat}
                    title={`${oneTrack.captions}\n\n${phrasePieceTitle}`}
                    style={{ fontSize: `${state.captionsSize[state.originalLang]}px` }}
                    onClick={handleClick}
                >
                    {phraseLineText}
                </PhraseContinuesSC>
            ) : (
                <ContentEditable
                    ariaLabel={`${isRightSide ? state.lang : state.originalLang}: ${oneTrack.original_id}`}
                    className="h-full"
                    // Also on the editable host itself, not just the box: the caret and the
                    // text typed into it follow the element's own direction.
                    dir={captionsDir}
                    style={{
                        fontSize: `${state.captionsSize[isRightSide ? state.lang : state.originalLang]}px`,
                        lineHeight: "normal",
                        overflow: "auto",
                        padding: "5px 10px",
                    }}
                    handleInput={(text, focused) => handleInput(text, focused, oneTrack)}
                    handleBlur={onBlur}
                    captions={isRightSide ? oneTrack.translation || "" : oneTrack.captions}
                    editing={editing}
                    contentEditable={!state.savingStatus && !hideEditors}
                    handleClick={handleClick}
                    comment={correction?.comment}
                />
            )}

            {isPhrasePiece && !continuesPhrase && (
                <PhrasePieceBadgeSC
                    data-phrase-piece="true"
                    data-phrase-repeat={isDubRepeat ? "true" : undefined}
                    $repeat={isDubRepeat}
                    title={phrasePieceTitle}
                >
                    {phraseBadgeText}
                </PhrasePieceBadgeSC>
            )}

            {!isRightSide && (
                <>
                    {!hideEditors && (
                        <div className="xt_textarea_actions">
                            <div className="x_save x_save--cancel" onClick={handleEditSubtitle}>
                                <Ico>edit</Ico>
                            </div>
                            <div className="x_save" onClick={() => handles.mergeCaptions(oneTrack)}>
                                <Ico>merge</Ico>
                            </div>
                            <CreateIntervalSubtitle track={oneTrack} />
                            {correction && (
                                <IconWrapperSC
                                    onClick={handleCheckTranslatedSubtitle}
                                    color={correction ? "green" : ""}
                                >
                                    <CheckNeedMIcon />
                                </IconWrapperSC>
                            )}
                        </div>
                    )}
                    <ActionsColumnSC>
                        <ActionButtonSC
                            data-skip-cue="true"
                            onClick={() => void handleCreateSkip()}
                            disabled={state.translatedSubtitleId === oneTrack.original_id || !!editing || skipped}
                            title={
                                skipped
                                    ? en
                                        ? "The cue is already removed from voicing — bring it back with ♪ or «restore» in the translation column"
                                        : "Реплика уже снята с озвучки — вернуть её можно кнопкой ♪ или «вернуть» в колонке перевода"
                                    : en
                                      ? "Remove from voicing — the cue's translation and voicing will not be in the video"
                                      : "Снять с озвучки — перевод и озвучка реплики не попадут в видео"
                            }
                        >
                            <Ico>block</Ico>
                        </ActionButtonSC>

                        <ActionButtonSC
                            onClick={() => void handleCreateTranslate()}
                            disabled={state.translatedSubtitleId === oneTrack.original_id || !!editing}
                            title={
                                en
                                    ? "Machine translation — replaces the text"
                                    : "Машинный перевод — заменит текст"
                            }
                        >
                            <ForwardMIcon fontSize={"1.3em"} />
                        </ActionButtonSC>
                    </ActionsColumnSC>
                </>
            )}

            {isRightSide && (
                <>
                    <div className="xt_textarea_actions">
                        <div className="x_save" onClick={handleClickRecommendation}>
                            <SpellCheckMIcon />
                        </div>
                        <div
                            className="x_save"
                            title="Разбить по переводу"
                            style={translatedBusy ? { opacity: 0.4, cursor: "no-drop" } : undefined}
                            onClick={handleOpenTranslatedSplit}
                        >
                            <Ico>edit</Ico>
                        </div>
                        <div
                            className="x_save"
                            title="Объединить перевод со второй выбранной репликой"
                            style={translatedBusy ? { opacity: 0.4, cursor: "no-drop" } : undefined}
                            onClick={handleMergeTranslated}
                        >
                            <Ico>merge</Ico>
                        </div>
                        <div
                            className="x_save"
                            title="Разбить по паузам лектора (Opus)"
                            style={{
                                fontWeight: 600,
                                fontSize: "0.8em",
                                ...(translatedBusy ? { opacity: 0.4, cursor: "no-drop" } : {}),
                            }}
                            onClick={handleOpusSplit}
                        >
                            Opus
                        </div>
                        <div className="x_save" onClick={handleDeleteTranslatedSubtitle}>
                            <Ico>delete</Ico>
                        </div>
                        <IconWrapperSC
                            onClick={handleCheckTranslatedSubtitle}
                            color={correction ? "green" : ""}
                        >
                            <CheckNeedMIcon />
                        </IconWrapperSC>
                    </div>
                    {translatedSplitOpen && (
                        <TranslatedSplitModal
                            oneTrack={oneTrack}
                            handleClose={() => setTranslatedSplitOpen(false)}
                        />
                    )}
                    {/* ♪ и 🔊 видны всегда, бледно (проба P50m20g, E6: значок ♪ был виден только при наведении). */}
                    <ActionsColumnSC $rest data-voicing-column="true">
                        <ActionButtonSC
                            disabled={
                                state.translatedSubtitleId === oneTrack.original_id ||
                                editing === oneTrack.original_id
                            }
                        >
                            <AudioManage
                                lang={state.lang}
                                subtitleId={oneTrack.original_id}
                                voice={oneTrack.voice}
                                disabled={
                                    state.translatedSubtitleId === oneTrack.original_id ||
                                    editing === oneTrack.original_id
                                }
                                currentSpeed={oneTrack.speed}
                                setEditing={setEditing}
                                // Нет пары перевода, пустой текст, испорченное окно — сервер
                                // откажет; ♪ и 🔊 говорят об этом до нажатия, а не после. Судят
                                // они ровно как сервер озвучки: по json перевода, длина дубля не
                                // в счёт, оригинальному звуку текст не нужен.
                                blockedReason={rowVoicingBlockReason(oneTrack, "tts", en)}
                                originalBlockedReason={rowVoicingBlockReason(oneTrack, "original_ru", en)}
                                // Сервер прослушал дубль короткой реплики и не услышал текста —
                                // ♪ помнит это и после того, как красная полоса закрыта.
                                checkWarning={rowVoicingCheckWarning(oneTrack, en)}
                            />
                        </ActionButtonSC>
                    </ActionsColumnSC>
                </>
            )}

            {isRightSide && !skipped && oneTrack.translation_speaker && (
                <SpeakerBadgeSC
                    data-speaker={oneTrack.translation_speaker.who}
                    data-speaker-voice={oneTrack.translation_speaker.voice}
                    $hall={oneTrack.translation_speaker.who === "hall"}
                    title={cueSpeakerTitle(oneTrack.translation_speaker, en)}
                >
                    {cueSpeakerLabel(oneTrack.translation_speaker, en)}
                </SpeakerBadgeSC>
            )}

            {isRightSide && skipped && (
                <SkippedSC data-skipped="true" title={skippedTitle}>
                    <span>⊘ {en ? "removed from voicing" : "снята с озвучки"}</span>
                    <span
                        data-unskip="true"
                        style={unskipping ? { opacity: 0.5, cursor: "wait" } : undefined}
                        onClick={(e) => void handleUnskip(e)}
                    >
                        {en ? "restore" : "вернуть"}
                    </span>
                </SkippedSC>
            )}

            {(sourceTimeBroken || translationTimeBroken) && (
                <BrokenTimeSC data-broken-time="true" title={brokenTimeTitle}>
                    <span>⚠ {en ? "time" : "время"}</span>
                    {canRestoreWindow && (
                        <span data-restore-window="true" onClick={handleRestoreWindow}>
                            {en ? "restore window" : "вернуть окно"}
                        </span>
                    )}
                </BrokenTimeSC>
            )}

            {oneTrack["trimmed_start"] && (
                <span style={{ color: "red" }}>⚠️ Beginning of fragment outside</span>
            )}
        </SubtitleContainerSC>
    );
};

function checkCorrectionTime({
    item,
    oneTrack,
    isRightSide,
    lang,
}: {
    item: Correction;
    oneTrack: AudioTrack;
    isRightSide: boolean;
    lang: string;
}) {
    if (isRightSide) {
        return (
            item.subtitle_start_time >= (oneTrack.translationStart || 0) &&
            item.subtitle_start_time <= (oneTrack.translationStart || 0) + oneTrack.length &&
            item.language === lang &&
            item.status !== "done" &&
            item.folder_num === oneTrack.folder_num.toString()
        );
    } else {
        return (
            item.subtitle_start_time >= oneTrack.start &&
            item.subtitle_start_time <= oneTrack.start + oneTrack.length &&
            item.language === lang &&
            item.status !== "done" &&
            item.folder_num === oneTrack.folder_num.toString()
        );
    }
}
